import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Car, Plus } from '@/ui/icons'
import { Button, Card, ConfirmationDialog, Skeleton } from '@/ui/components'
import { EmptyState, PageHeader } from '@/ui/design-system'
import { PageLayout } from '@/ui/layout'
import { cn } from '@/shared/utils'
import type { VehicleProfile } from '@/shared/api/vehicles'
import { useAuth } from '@/modules/auth'
import { useUserDisplayName } from '@/modules/users/hooks/useUserDisplayName'
import { VehicleEditModal, VehicleModificationsProfile, VehicleSpecs } from '@/modules/vehicles/components'
import { useVehicles } from '@/modules/vehicles/hooks/useVehicles'
import { getVehicleSubtitle, getVehicleTitle } from '@/modules/vehicles/utils/vehicleLabels'

export function GaragePage() {
  const { userId } = useParams<{ userId: string }>()
  const { user } = useAuth()
  const ownerId = userId ?? user?.id

  const { vehicles, primaryVehicle, isOwner, isLoading, error, refetch, deleteVehicle, setPrimaryVehicle, isDeleting } =
    useVehicles(ownerId)
  const { displayName } = useUserDisplayName(isOwner ? undefined : ownerId)

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const [vehicleBeingEdited, setVehicleBeingEdited] = useState<VehicleProfile | undefined>()
  const [vehicleBeingDeleted, setVehicleBeingDeleted] = useState<VehicleProfile | null>(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  // Going from one person's garage to another must not carry the selection over.
  useEffect(() => {
    setSelectedId(null)
  }, [ownerId])

  const selectedVehicle = vehicles.find((vehicle) => vehicle.id === selectedId) ?? primaryVehicle

  const openAddVehicle = () => {
    setVehicleBeingEdited(undefined)
    setIsEditorOpen(true)
  }

  const openEditVehicle = (vehicle: VehicleProfile) => {
    setVehicleBeingEdited(vehicle)
    setIsEditorOpen(true)
  }

  // The vehicle stays set while the dialog closes, so its title doesn't change mid-animation.
  const openDeleteVehicle = (vehicle: VehicleProfile) => {
    setVehicleBeingDeleted(vehicle)
    setIsDeleteDialogOpen(true)
  }

  const handleSetPrimary = async (vehicle: VehicleProfile) => {
    setActionError(null)
    try {
      await setPrimaryVehicle(vehicle.id)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "That vehicle couldn't be made primary.")
    }
  }

  const handleConfirmDelete = async () => {
    if (!vehicleBeingDeleted) return
    setActionError(null)
    try {
      await deleteVehicle(vehicleBeingDeleted.id)
      setSelectedId(null)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "That vehicle couldn't be deleted.")
    } finally {
      setIsDeleteDialogOpen(false)
    }
  }

  const vehicleCount = `${vehicles.length} ${vehicles.length === 1 ? 'vehicle' : 'vehicles'}`
  const title = isOwner ? 'Your garage' : displayName ? `${displayName}'s garage` : 'Garage'
  const subtitle =
    isLoading || error || vehicles.length === 0
      ? undefined
      : isOwner
        ? `${vehicleCount}. Your primary one shows on trip rosters.`
        : vehicleCount

  return (
    <PageLayout>
      <div className="min-h-screen w-full bg-background">
        <div className="mx-auto w-full max-w-5xl px-4 pb-24 pt-6 md:px-8 md:pb-10 md:pt-8">
          <PageHeader
            title={title}
            subtitle={subtitle}
            className="mb-6 gap-4"
            actions={
              <>
                {ownerId && (
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/users/${ownerId}`}>View profile</Link>
                  </Button>
                )}
                {isOwner && vehicles.length > 0 && (
                  <Button size="sm" onClick={openAddVehicle} className="gap-1.5">
                    <Plus className="h-4 w-4" aria-hidden />
                    Add vehicle
                  </Button>
                )}
              </>
            }
          />

          {actionError && (
            <p role="alert" className="mb-4 rounded-md border border-error/30 bg-error/10 px-3 py-2 text-sm text-error">
              {actionError}
            </p>
          )}

          {isLoading ? (
            <div className="space-y-4" aria-hidden>
              <Skeleton className="h-52 w-full rounded-lg" />
              <Skeleton className="h-72 w-full rounded-lg" />
            </div>
          ) : error ? (
            <Card className="border-border bg-card p-6 shadow-none" role="alert">
              <p className="text-sm text-foreground">This garage didn't load.</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
                Try again
              </Button>
            </Card>
          ) : !selectedVehicle ? (
            isOwner ? (
              <EmptyState
                icon={<Car className="mx-auto h-8 w-8 text-earth-500" aria-hidden />}
                title="No vehicles yet"
                description="Add your rig to show specs, mods, and gear. Your crew will see it when you're on a trip together."
                action={{ label: 'Add your first vehicle', onClick: openAddVehicle }}
              />
            ) : (
              <EmptyState
                icon={<Car className="mx-auto h-8 w-8 text-earth-500" aria-hidden />}
                title="No vehicles added yet"
              />
            )
          ) : (
            <div className={cn('flex flex-col gap-4', vehicles.length > 1 && 'md:flex-row md:items-start md:gap-6')}>
              {vehicles.length > 1 && (
                <nav
                  aria-label="Vehicles"
                  className="-mx-4 flex gap-2 overflow-x-auto px-4 [scrollbar-width:none] md:mx-0 md:w-56 md:flex-shrink-0 md:flex-col md:gap-1 md:overflow-visible md:px-0"
                >
                  {vehicles.map((vehicle) => {
                    const isSelected = vehicle.id === selectedVehicle.id
                    const vehicleSubtitle = getVehicleSubtitle(vehicle)
                    return (
                      <button
                        key={vehicle.id}
                        type="button"
                        onClick={() => setSelectedId(vehicle.id)}
                        aria-current={isSelected ? 'true' : undefined}
                        className={cn(
                          'w-48 flex-shrink-0 rounded-md px-3 py-2 text-left transition-[background-color,transform] duration-150 ease-out active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring md:w-full',
                          isSelected ? 'bg-earth-100' : 'hover:bg-earth-50'
                        )}
                      >
                        <span className="block truncate text-sm font-medium text-foreground">
                          {getVehicleTitle(vehicle)}
                        </span>
                        {(vehicleSubtitle || vehicle.is_primary) && (
                          <span className="block truncate text-xs text-earth-600">
                            {[vehicleSubtitle, vehicle.is_primary ? 'Primary' : null].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </button>
                    )
                  })}
                </nav>
              )}

              <div className="min-w-0 flex-1 space-y-4">
                <VehicleSpecs
                  vehicle={selectedVehicle}
                  onEdit={isOwner ? openEditVehicle : undefined}
                  onSetPrimary={isOwner ? handleSetPrimary : undefined}
                  onDelete={isOwner ? openDeleteVehicle : undefined}
                />
                <VehicleModificationsProfile vehicleId={selectedVehicle.id} canEdit={isOwner} />
              </div>
            </div>
          )}
        </div>
      </div>

      {isOwner && (
        <>
          <VehicleEditModal
            isOpen={isEditorOpen}
            onClose={() => setIsEditorOpen(false)}
            vehicle={vehicleBeingEdited}
            onSaved={(saved) => setSelectedId(saved.id)}
          />
          <ConfirmationDialog
            open={isDeleteDialogOpen}
            onOpenChange={setIsDeleteDialogOpen}
            title={vehicleBeingDeleted ? `Delete ${getVehicleTitle(vehicleBeingDeleted)}?` : 'Delete vehicle?'}
            description="Its build list goes with it. This can't be undone."
            confirmText="Delete vehicle"
            variant="destructive"
            isLoading={isDeleting}
            onConfirm={handleConfirmDelete}
          />
        </>
      )}
    </PageLayout>
  )
}
